import { observer } from 'mobx-react-lite';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  Icon,
  IconButton,
  Spacer,
  TextBody,
  TextTitle,
  type IconName,
} from '@pypestream/design-system';

import { tableStore } from '../stores/table-store';
import { solutionAssistantStore } from '../stores/solution-assistant-store';
import { useBobcatFlag } from '../hooks/use-bobcat-flag';
import { useProStudioRoutes, useProStudioViewerRole } from '../hooks';
import { useProStudioCtxSelector } from '../xstate/app.xstate';
import { getChannelsFromPypes } from '../utils';
import { buildCanvasAgentNodes } from './canvas-view/canvas-view-model';
import { ChannelKind, DesignChannelPanel } from './design-channel-panel';
import { AgentSettingsPanel } from './agent-settings/agent-settings-panel';
import { withoutSettingsBlock } from './agent-settings/agent-settings-model';
import { agentSettingsStore } from '../stores/agent-settings-store';
import { DesignAgents } from './design-agents/design-agents';

import { SolutionSetup } from './solution-setup';
import { SolutionNameEditor } from './solution-name-editor';
import { clearWantsVoice, wantsVoice } from './talk/wants-voice';
import { useHasLiveAgentPlatform } from '../hooks/use-has-live-agent-platform';
import {
  findEscalationRow,
  handoffOnFields,
  handoffState,
} from '../utils/live-agent-handoff';
import pypeMark from '../assets/home/pype-mark.svg?url';
import voiceMark from '../assets/home/voice-mark.svg?url';
import './solution-overview.scss';

const ENGAGEMENT_API_DOCS_URL =
  'https://developers.pypestream.com/reference/engagement-overview';

/**
 * A channel type as the prototype's tool tile (`renderChannels`): its mark,
 * name, one line of description and how many channels run on it, with a
 * purple check and border once any do. A type with none is listed, not
 * asserted.
 */
const ChannelSummaryCard: FC<{
  /** The type's own mark (Pype, Voice), or an icon when it has none. */
  logo?: string;
  icon?: IconName;
  title: string;
  description?: string;
  /** `undefined` when there's no real count to show — an informational-only
   *  card (see "Other channels" below), not a real, clickable summary. */
  count?: number;
  selected?: boolean;
  onClick?: () => void;
}> = ({ logo, icon, title, description, count, selected, onClick }) => {
  const Tag = onClick ? 'button' : 'div';
  const on = !!count;
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      className={[
        'c-solution-overview__channel-card',
        !on && 'c-solution-overview__channel-card--off',
        selected && 'c-solution-overview__channel-card--selected',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-pressed={onClick ? !!selected : undefined}
      onClick={onClick}
      data-testid={`bobcat-solution-overview-channel-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {on && (
        <span className="c-solution-overview__channel-check" aria-hidden>
          <Icon name="check" size="xsmall" />
        </span>
      )}
      <span className="c-solution-overview__channel-mark">
        {logo ? (
          <img src={logo} alt="" width={28} height={28} />
        ) : (
          icon && <Icon name={icon} size="medium" />
        )}
      </span>
      <span className="c-solution-overview__channel-name">{title}</span>
      {description && (
        <span className="c-solution-overview__channel-desc">{description}</span>
      )}
      {count !== undefined && (
        <span className="c-solution-overview__channel-count">
          {count} channel{count === 1 ? '' : 's'}
        </span>
      )}
    </Tag>
  );
};

/**
 * Project Bobcat, 5-stage nav Phase 2 — the Design stage's real content,
 * replacing its placeholder. "Solution Overview... the default landing view
 * for Design — a persistent front door, not a one-time modal" (real
 * prototype copy, `prostudio-build-flow-layouts.html`, checked live — see
 * "PR Spec: 5-Stage Nav").
 *
 * Every field here is real, already-existing data — this page adds no new
 * LLM call and no new data source:
 * - Agents: the exact same `buildCanvasAgentNodes` canvas's own sidebar
 *   reads — every agent, as pills in their canvas colors, two lines at most
 *   with the rest behind "See N more" (`design-agents/`). Choosing one opens
 *   its rows in the Build table.
 * - Channels: `getChannelsFromPypes`, the same real data `pages/channels.tsx`
 *   already lists. Pro Studio's own `Channel.type` is only `'widget' |
 *   'twilio_voice'` (`get-channels-from-pypes.ts`) — there is no real data
 *   source here for the prototype's third "Other channels" (SMS/WhatsApp)
 *   card, which its own copy says is "configured in Contact Center" — so
 *   that card is informational only, no count, and does not link anywhere.
 *   Clicking a real channel-type card opens `DesignChannelPanel`, which
 *   carries the Channels and Default Messages pages' actions (those pages
 *   redirect here with the 5-stage nav on).
 * - Global Instructions: `ctx.draftVersionSolutionData.knowledgeAgentContext`
 *   — the exact field `tab-agent.tsx`'s Knowledge Agent global-settings panel
 *   (the real SOP front door, behind the master)
 *   reads and writes. Displayed read-only here; an empty value shows an
 *   honest empty state, never a fake example.
 * - The solution's name, renamed in place (`solution-name-editor.tsx`).
 *   Brand, tone and global instructions are edited under Agent settings.
 * - "Build your own" / Engagement API: links to the public EAPI docs.
 */
export const SolutionOverview: FC = observer(() => {
  const [openKind, setOpenKind] = useState<ChannelKind | null>(null);

  const navigate = useNavigate();
  const routes = useProStudioRoutes();
  const isViewer = useProStudioViewerRole();
  const [voiceDismissed, setVoiceDismissed] = useState(false);
  // `null` while the search is closed; the text once it's open.
  const [agentSearch, setAgentSearch] = useState<string | null>(null);
  const { uniqueId, botPypes, knowledgeAgentContext } = useProStudioCtxSelector(
    (ctx) => ({
      uniqueId: ctx.selectedBot?.uniqueId,
      botPypes: ctx.botPypes || [],
      knowledgeAgentContext:
        ctx.draftVersionSolutionData?.knowledgeAgentContext,
    })
  );

  const rows = tableStore.getAllSortedRows();
  const agents = buildCanvasAgentNodes(rows);

  // Live-agent handoff (Edan's review, Oct 27): what a customer who asks for
  // a person gets, and the way on when there is no platform to transfer to.
  const projectId = useProStudioCtxSelector((ctx) => ctx.project?.id);
  const hasPlatform = useHasLiveAgentPlatform();
  const handoff = handoffState(hasPlatform, rows);
  const turnOnHandoff = () => {
    const row = findEscalationRow(rows);
    if (!row) return;
    tableStore.updateRow({
      rowId: row.id,
      updates: handoffOnFields(row) as Partial<(typeof rows)[number]>,
      saveImmediately: true,
    });
  };

  const channels = getChannelsFromPypes(botPypes);
  const pypeChannels = channels.filter((c) => c.type !== 'twilio_voice');
  const voiceChannels = channels.filter((c) => c.type === 'twilio_voice');
  // Asked for in "Talk it through", but creating only makes Pype channels.
  const offerVoice =
    !isViewer &&
    !voiceDismissed &&
    voiceChannels.length === 0 &&
    wantsVoice(uniqueId);

  // One panel on the right at a time: a channel type, or Agent settings.
  const toggleKind = (kind: ChannelKind) => {
    agentSettingsStore.close();
    setOpenKind((prev) => (prev === kind ? null : kind));
  };
  const openAgentSettings = () => {
    setOpenKind(null);
    // With the AI dock on, Agent settings IS its Agents tab.
    if (fiveStageNavOn) {
      agentSettingsStore.close();
      solutionAssistantStore.openTab('agents');
      return;
    }
    agentSettingsStore.show();
  };
  // Opened from elsewhere (the assistant applying a change) while a channel
  // panel is up: the channel panel yields, or the settings would not show.
  // Read during render (this component is an observer), so the effect
  // re-runs when the store opens.
  const settingsOpen = agentSettingsStore.open;
  useEffect(() => {
    if (settingsOpen) setOpenKind(null);
  }, [settingsOpen]);
  const fiveStageNavOn = useBobcatFlag('fiveStageNav') === 'allowed';

  return (
    <div className="c-design-stage">
      <div className="c-design-stage__main">
        <div
          className="c-solution-overview"
          data-testid="bobcat-solution-overview"
        >
          <TextTitle size="small">Solution Overview</TextTitle>

          <Spacer size="large" />

          <div className="c-solution-overview__card">
            <span className="c-solution-overview__eyebrow">Solution</span>
            <SolutionNameEditor />

            <Spacer size="medium" />
            <div className="c-solution-overview__agents-head">
              <span className="c-solution-overview__eyebrow c-solution-overview__eyebrow--inline">
                Agents
              </span>
              {agentSearch === null ? (
                <IconButton
                  name="search"
                  size="small"
                  variant="tertiary"
                  aria-label="Search agents"
                  test-id="bobcat-solution-overview-agent-search"
                  onClick={() => setAgentSearch('')}
                />
              ) : (
                <input
                  className="c-solution-overview__agent-search"
                  type="search"
                  placeholder="Search agents…"
                  aria-label="Search agents"
                  value={agentSearch}
                  data-testid="bobcat-solution-overview-agent-search-input"
                  // Opened by a click, so focus follows it there.
                  autoFocus
                  onChange={(e) => setAgentSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setAgentSearch(null);
                  }}
                  onBlur={() => {
                    if (!agentSearch) setAgentSearch(null);
                  }}
                />
              )}
              <span className="c-solution-overview__agents-spacer" />
              <Button
                variant="ghost"
                size="small"
                test-id="bobcat-solution-overview-agent-settings"
                onClick={openAgentSettings}
              >
                <Icon name="settings" size="xsmall" slot="prefix" />
                Agent settings
              </Button>
            </div>
            <Spacer size="xsmall" />
            <DesignAgents
              agents={agents}
              filter={agentSearch ?? ''}
              // The canvas when it's on (it's where agents are drawn);
              // otherwise the agent's rows in the table, as before.
              onOpenAgent={(agent) =>
                fiveStageNavOn
                  ? tableStore.openAgentOnCanvas(agent.id)
                  : tableStore.openAgentInBuild(agent.id)
              }
            />

            <Spacer size="large" />
            <span className="c-solution-overview__eyebrow">Channels</span>
            <div className="c-solution-overview__channels">
              <ChannelSummaryCard
                logo={pypeMark}
                title="Pype"
                count={pypeChannels.length}
                selected={openKind === 'pype'}
                onClick={() => toggleKind('pype')}
              />
              <ChannelSummaryCard
                logo={voiceMark}
                title="Voice"
                count={voiceChannels.length}
                selected={openKind === 'voice'}
                onClick={() => toggleKind('voice')}
              />
              <ChannelSummaryCard
                icon="comments"
                title="Other channels"
                description="SMS and WhatsApp, configured in Contact Center."
              />
            </div>
            {offerVoice && (
              <div
                className="c-solution-overview__voice-offer"
                data-testid="bobcat-solution-overview-voice-offer"
              >
                <Icon name="call" size="small" />
                <TextBody size="small">
                  Add a Voice channel to give it a phone number.
                </TextBody>
                <Button
                  size="small"
                  test-id="bobcat-solution-overview-add-voice"
                  onClick={() => navigate(routes.voice_channel_creation.path)}
                >
                  Add Voice channel
                </Button>
                <Button
                  size="small"
                  variant="ghost"
                  onClick={() => {
                    if (uniqueId) clearWantsVoice(uniqueId);
                    setVoiceDismissed(true);
                  }}
                >
                  Not now
                </Button>
              </div>
            )}

            {/* A link out, not a tile: the whole row goes to the docs. */}
            <a
              className="c-solution-overview__engagement-api"
              data-testid="bobcat-solution-overview-eapi-docs"
              href={ENGAGEMENT_API_DOCS_URL}
              target="_blank"
              rel="noreferrer"
            >
              <span className="c-solution-overview__engagement-api-mark">
                <Icon name="brackets" size="small" />
              </span>
              <span className="c-solution-overview__engagement-api-body">
                <span className="c-solution-overview__engagement-api-title">
                  Build your own
                </span>
                <span className="c-solution-overview__engagement-api-desc">
                  Reach this solution from your own app or back end with the
                  Engagement API.
                </span>
              </span>
              <span className="c-solution-overview__docs-link">
                Documentation
                <Icon name="external-link" size="xsmall" />
              </span>
            </a>

            {(handoff === 'no-platform' ||
              handoff === 'off' ||
              handoff === 'on') && (
              <div
                className="c-solution-overview__engagement-api c-solution-overview__handoff"
                data-testid="bobcat-solution-overview-handoff"
              >
                <span className="c-solution-overview__engagement-api-mark">
                  <Icon name="people-group" size="small" />
                </span>
                <span className="c-solution-overview__engagement-api-body">
                  <span className="c-solution-overview__engagement-api-title">
                    {handoff === 'no-platform'
                      ? 'Live-agent handoff: no platform connected'
                      : handoff === 'off'
                        ? 'Live-agent handoff: off'
                        : 'Live-agent handoff'}
                  </span>
                  <span className="c-solution-overview__engagement-api-desc">
                    {handoff === 'no-platform'
                      ? 'When a customer asks for a person, the solution says no one is available right now and keeps helping.'
                      : handoff === 'off'
                        ? 'A live agent platform is connected. Customers who ask for a person still hear that no one is available.'
                        : 'Customers who ask for a person are transferred to a live agent.'}
                  </span>
                </span>
                {handoff === 'off' && !isViewer ? (
                  <Button
                    size="small"
                    variant="secondary"
                    data-testid="bobcat-solution-overview-handoff-on"
                    onClick={turnOnHandoff}
                  >
                    Turn on handoff
                  </Button>
                ) : (
                  projectId && (
                    <button
                      type="button"
                      className="c-solution-overview__docs-link c-solution-overview__handoff-link"
                      onClick={() =>
                        navigate(`/${projectId}/settings/live-agent-platforms`)
                      }
                    >
                      {handoff === 'no-platform'
                        ? 'Set up a live agent platform'
                        : 'Manage platforms'}
                      <Icon name="arrow-right" size="xsmall" />
                    </button>
                  )
                )}
              </div>
            )}

            <Spacer size="large" />
            <span className="c-solution-overview__eyebrow c-solution-overview__eyebrow--ai">
              <Icon name="stars" size="xsmall" />
              Global instructions — AI-drafted from your procedure
            </span>
            <p className="c-solution-overview__prose">
              {/* The Agent settings block is shown in its own panel. */}
              {withoutSettingsBlock(knowledgeAgentContext) ||
                (fiveStageNavOn
                  ? 'No global instructions set yet — add them under Agent settings → Knowledge Agent.'
                  : 'No global instructions set yet — add them from the Knowledge Agent panel in Table View.')}
            </p>

            <Spacer size="medium" />
            <SolutionSetup />
          </div>
        </div>
      </div>
      {!fiveStageNavOn && agentSettingsStore.open && !openKind && (
        <AgentSettingsPanel />
      )}
      {openKind && (
        <DesignChannelPanel
          kind={openKind}
          channels={openKind === 'voice' ? voiceChannels : pypeChannels}
          onClose={() => setOpenKind(null)}
        />
      )}
    </div>
  );
});
